import { useTranslations } from '@vocab/react';
import {
  Alert,
  Badge,
  Box,
  Heading,
  IconTick,
  Inline,
  List,
  Stack,
  Text,
} from 'braid-design-system';
import {
  type FunctionComponent,
  useEffect,
  type ReactNode,
  useContext,
} from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { useLocalStorage } from '../../hooks';
import type { State } from '../App/state';
import CertsyTooltip from '../CertsyTooltip/CertsyTooltip';
import ContextualHelp from '../ContextualHelp/ContextualHelp';
import { useFeatureFlag } from '../FeatureFlag/FeatureFlag';
import { IconSeekPass } from '../Icons';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

import * as styles from './Header.css';

type Props = Pick<
  State,
  | 'settings'
  | 'selectionLimit'
  | 'published'
  | 'questionnaireHasQuestions'
  | 'questions'
  | 'showQuestions'
>;

export const Header: FunctionComponent<Props> = ({
  settings: { questionSectionHeading, showCertsyTooltip },
  selectionLimit,
  published,
  questionnaireHasQuestions,
  questions,
  showQuestions,
}) => {
  const { t } = useTranslations(translations);
  const [showMustHaveAlertLocalStorage, setShowMustHaveLocalStorage] =
    useLocalStorage('essentialQuestionAlert', true);

  useEffect(() => {
    if (published) {
      setShowMustHaveLocalStorage(false);
    } else {
      setShowMustHaveLocalStorage(true);
    }
  }, [published, setShowMustHaveLocalStorage, showMustHaveAlertLocalStorage]);
  const analytics = useContext(AnalyticsContext);

  const showNewPill = useFeatureFlag('newPill');
  const showContextualHelp = useFeatureFlag('help');
  const showHeader = useFeatureFlag('showHeader');
  const showPublishedHeaderAlert = useFeatureFlag(
    'showPublishedHeaderAlert',
    true,
  );
  const showMustHaveAlertFromFeatureFlags = useFeatureFlag('mustHaveNew');

  const mandatoryQuestions = questions
    ? Object.values(questions).filter((question) => question.mandatory)
    : [];

  const getInfoText = () =>
    questionnaireHasQuestions
      ? t('header.published.info')
      : t('header.published.emptyQuestionInfo');

  const shouldShowCPQASection = mandatoryQuestions.length > 0 && showQuestions;

  useEffect(() => {
    if (shouldShowCPQASection) {
      analytics.sendCPQAQuestionsDisplayedEvent();
    }
  }, [analytics, shouldShowCPQASection]);

  return (
    <Stack space="medium">
      {showHeader ? (
        <Box
          className={styles.container}
          data={{ intercomid: 'rr-selection-heading' }}
        >
          <Inline space="xsmall" alignY="center">
            <Heading level="4" component="h3">
              {questionSectionHeading
                ? t('header.newTitle')
                : t('header.title')}
            </Heading>
            {showNewPill && <Badge tone="positive">{t('header.new')}</Badge>}
            {showContextualHelp && (
              <ContextualHelp showCertsyTooltip={showCertsyTooltip ?? false} />
            )}
          </Inline>
        </Box>
      ) : null}
      {showMustHaveAlertLocalStorage && showMustHaveAlertFromFeatureFlags && (
        <Alert
          tone="info"
          onClose={() => {
            setShowMustHaveLocalStorage(false);
          }}
          closeLabel="Close"
        >
          <Stack space="medium">
            <Text>
              {t('header.mustHave', {
                strong: (children: ReactNode) => <strong>{children}</strong>,
              })}
            </Text>
          </Stack>
        </Alert>
      )}
      {published && showPublishedHeaderAlert && (
        <Alert tone="info">
          <Stack space="medium">
            <Text>{t('header.published.alert')}</Text>
          </Stack>
        </Alert>
      )}

      <Text>
        {published
          ? getInfoText()
          : t('header.sell.musthave', {
              selectionLimit: selectionLimit.toString(),
              p: (children: ReactNode) => (
                <>
                  {children}
                  <br />
                </>
              ),
            })}
      </Text>

      {shouldShowCPQASection && (
        <Box
          boxShadow="borderNeutralLight"
          borderRadius="large"
          marginTop="xxsmall"
          className={styles.ppaCard}
        >
          <Stack space="large">
            <Stack space="small">
              <Badge tone="promote">{t('header.cpqa.badge')}</Badge>
              <Text weight="medium" component="h5">
                {t('header.cpqa.title')}
              </Text>
              <Text size="small">{t('header.cpqa.description')}</Text>
            </Stack>
            <Stack space="medium">
              {mandatoryQuestions.map((question) => (
                <Stack space="small" key={question.id}>
                  <Inline space="xxsmall" alignY="center">
                    <Text size="large">
                      <IconTick tone="promote" />
                    </Text>
                    <Text weight="medium" size="small">
                      {question.statement}{' '}
                      {question.certsyVerifiable && (
                        <CertsyTooltip>
                          <IconSeekPass />
                        </CertsyTooltip>
                      )}
                    </Text>
                  </Inline>

                  <Box paddingLeft="xxsmall">
                    <List size="small" space="small">
                      {question.minimumRequiredAnswers?.map((answer) => (
                        <Box key={answer.id} className={styles.ppaAnswers}>
                          <Text>
                            {answer.text}{' '}
                            {question.description === 'RangeAtLeast' &&
                              'or more'}
                          </Text>
                        </Box>
                      ))}
                    </List>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
